import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { PrismLoading, PrismButton, PrismBanner, PrismCheckbox } from '@prism-ui/react';
import { onCheckedChange } from '../../../../utils/prismCheckbox';
import PrismDataGrid, {
  type DataGridColumn,
} from '../../../shared/PrismDataGrid';
import { DateTime } from 'luxon';
import {
  formatUtcTimestamp,
  formatLocalTimestamp,
  viewerIsUtc,
} from '../../../../util/dateUtils';
import ExcelJS from 'exceljs';
import clearLogoSrc from '../../../../assets/images/clear-logo.svg';
import './AuditLogs.css';
import AdminPane from '../../AdminPane';

interface AuditLog {
  id: number;
  source_user_id: number;
  source_user_name: string;
  target_table: string;
  target_table_row_id: number;
  target_user_name: string | null;
  target_table_column: string;
  previous_value: string | null;
  new_value: string | null;
  impersonating_user_id: number | null;
  impersonating_user_name: string | null;
  created_timestamp: string;
}

interface Tag {
  id: number;
  name: string;
}

// Helper function to format audit log values for display
const formatAuditLogValue = (value: string | null, targetTable: string, targetColumn: string, tags?: Tag[]): string => {
  if (!value) return '(empty)';
  
  // For project_tags tag_id column, show tag name instead of ID
  if (targetTable === 'project_tags' && targetColumn === 'tag_id' && tags) {
    const tagId = parseInt(value, 10);
    const tag = tags.find(t => t.id === tagId);
    if (tag) {
      return tag.name;
    }
    return `Tag ID ${value} (deleted)`;
  }
  
  // For user_role_year created/deleted operations, show friendly text instead of JSON
  if (targetTable === 'user_role_year' && (targetColumn === 'created' || targetColumn === 'deleted')) {
    try {
      JSON.parse(value);
      return '(User Role)';
    } catch (e) {
      // Not JSON, display as-is
    }
  }
  
  // For application_windows, strip time from ISO date strings
  if (targetTable === 'application_windows' && value.includes('T')) {
    return value.split('T')[0];
  }
  
  // For admin_settings, show friendly names for boolean values
  if (targetTable === 'admin_settings' && targetColumn === 'setting_value') {
    if (value === 'true') return 'Enabled';
    if (value === 'false') return 'Disabled';
  }
  
  return value;
};

// Helper function to format target names for admin settings
const formatTargetName = (targetName: string | null, targetTable: string): string => {
  if (!targetName) return '-';
  
  // For admin_settings, show friendly names for setting keys
  if (targetTable === 'admin_settings') {
    const friendlyNames: { [key: string]: string } = {
      'officer_login_as_enabled': 'User Impersonation',
      'officer_roles_sort_order': 'Officer Roles Sort Order',
    };
    return friendlyNames[targetName] || targetName;
  }
  
  return targetName;
};

// Wraps cell content and toggles between truncated and full wrapped text on click.
// These cells render inside prism-table's shadow root, where a floating popover would be
// clipped, so the full value is revealed in place instead.
const OverflowCell: React.FC<{ content: string }> = ({ content }) => {
  const cellRef = useRef<HTMLSpanElement>(null);
  const [expanded, setExpanded] = useState(false);

  const isOverflowing = () => {
    if (!cellRef.current) return false;
    return cellRef.current.scrollWidth > cellRef.current.clientWidth;
  };

  const handleClick = () => {
    // Once expanded the element no longer overflows, so allow collapsing unconditionally.
    if (expanded || isOverflowing()) {
      setExpanded(!expanded);
    }
  };

  return (
    <span
      ref={cellRef}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      role="button"
      tabIndex={0}
      style={
        expanded
          ? {
              cursor: 'pointer',
              display: 'block',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            }
          : {
              cursor: 'pointer',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }
      }
      title={expanded ? 'Click to collapse' : 'Click to view full text'}
    >
      {content}
    </span>
  );
};

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [tableFilter, setTableFilter] = useState<string[]>([]);
  const [columnFilter, setColumnFilter] = useState<string[]>([]);
  // Remounting the grid is how "Clear All Filters" resets prism-table's per-column filters.
  // As of 9.2, getTable().resetColumnFilters() clears the filter state but the text and select
  // filter inputs are uncontrolled and keep showing the old text (see Prism-Component-Gaps).
  const [gridKey, setGridKey] = useState<number>(0);
  const [filteredCount, setFilteredCount] = useState<number>(0);
  const filteredDataRef = useRef<AuditLog[]>([]);

  useEffect(() => {
    fetchAuditLogs();
    fetchTags();
  }, []);

  // Table and Column multi-select filters are applied in React rather than inside the
  // grid: prism-table renders its own filter UI and cannot host a custom header filter.
  const visibleLogs = useMemo(() => {
    return logs.filter((log) => {
      if (tableFilter.length && !tableFilter.includes(log.target_table)) return false;
      if (columnFilter.length && !columnFilter.includes(log.target_table_column))
        return false;
      return true;
    });
  }, [logs, tableFilter, columnFilter]);

  const handleFilteredRowsChange = useCallback((rows: AuditLog[]) => {
    filteredDataRef.current = rows;
    setFilteredCount(rows.length);
  }, []);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/audit_logs');
      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }
      const data = await response.json();
      setLogs(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch('/api/tags');
      if (!response.ok) {
        throw new Error('Failed to fetch tags');
      }
      const data = await response.json();
      setTags(data);
    } catch (err) {
      console.error('Error fetching tags for audit log display:', err);
      // Don't set error state - tags are optional for audit log display
    }
  };

  const uniqueTables = useMemo(() => {
    return Array.from(new Set(logs.map((log: AuditLog) => log.target_table))).sort();
  }, [logs]);

  // Column options reflect the rows surviving the Table filter, so the two
  // multi-selects stay coherent with each other.
  const uniqueColumns = useMemo(() => {
    const scoped = tableFilter.length
      ? logs.filter((log) => tableFilter.includes(log.target_table))
      : logs;
    return Array.from(
      new Set(scoped.map((log: AuditLog) => log.target_table_column).filter(Boolean))
    ).sort();
  }, [logs, tableFilter]);

  const MultiSelectFilter = ({
    selected,
    onChange,
    options,
    allLabel,
    filterIdPrefix,
  }: {
    selected: string[];
    onChange: (next: string[]) => void;
    options: string[];
    allLabel: string;
    filterIdPrefix: string;
  }) => {
    const [show, setShow] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleCheckboxChange = (item: string) => {
      onChange(
        selected.includes(item)
          ? selected.filter((t: string) => t !== item)
          : [...selected, item]
      );
    };

    const handleClearAll = () => onChange([]);

    const handleMouseEnter = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const handleMouseLeave = () => {
      timeoutRef.current = setTimeout(() => {
        setShow(false);
      }, 1000);
    };

    const displayText =
      selected.length === 0
        ? allLabel
        : selected.length === 1
          ? selected[0]
          : `${selected.length} selected`;

    return (
      <div
        className="table-filter-dropdown"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <PrismButton
          tone="secondary"
          display="outline"
          size="sm"
          className="table-filter-toggle"
          aria-expanded={show}
          onPrismClick={() => setShow(!show)}
        >
          {displayText}
        </PrismButton>
        {show && (
          <div className="table-filter-menu">
            <div className="table-filter-menu-head">
              <button
                type="button"
                className="table-filter-clear"
                onClick={handleClearAll}
                disabled={selected.length === 0}
              >
                Clear All
              </button>
            </div>
            <div className="table-filter-divider" />
            <div className="table-filter-options">
              {options.map((item: string) => (
                <PrismCheckbox
                  key={item}
                  id={`${filterIdPrefix}-${item}`}
                  label={item}
                  checked={selected.includes(item)}
                  onPrismChange={onCheckedChange(selected.includes(item), () => handleCheckboxChange(item))}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const columns = useMemo<Array<DataGridColumn<AuditLog>>>(() => [
    {
      id: 'created_timestamp',
      header: 'Timestamp (UTC)',
      sortable: true,
      accessor: (row) => row.created_timestamp,
      cell: (row) => (
        <div className="clr:flex clr:flex-col">
          <span>{formatUtcTimestamp(row.created_timestamp)}</span>
          {!viewerIsUtc() && (
            <span className="clr:prism-text-caption clr:text-secondary-base">
              {formatLocalTimestamp(row.created_timestamp)}
            </span>
          )}
        </div>
      ),
      filter: 'text',
      filterPlaceholder: 'e.g., Feb 14, 2026',
      // Filter against both renderings, so searching for the time you can see on screen
      // works whichever of the two lines you read it from.
      filterValue: (row) =>
        `${formatUtcTimestamp(row.created_timestamp)} ${formatLocalTimestamp(row.created_timestamp)}`,
      width: '210px',
    },
    {
      id: 'source_user_name',
      header: 'User',
      sortable: true,
      accessor: (row) => row.source_user_name,
      filter: 'text',
      filterPlaceholder: 'Filter...',
      width: '150px',
    },
    {
      id: 'target_table',
      header: 'Table',
      sortable: true,
      accessor: (row) => row.target_table,
      width: '130px',
    },
    {
      id: 'target_table_column',
      header: 'Column',
      sortable: true,
      accessor: (row) => row.target_table_column,
      width: '120px',
    },
    {
      id: 'target_user_name',
      header: 'Target',
      sortable: true,
      accessor: (row) => formatTargetName(row.target_user_name, row.target_table),
      filter: 'text',
      filterPlaceholder: 'Filter...',
      cell: (row) => (
        <OverflowCell content={formatTargetName(row.target_user_name, row.target_table)} />
      ),
      width: '150px',
    },
    {
      id: 'previous_value',
      header: 'Previous',
      sortable: true,
      accessor: (row) =>
        formatAuditLogValue(row.previous_value, row.target_table, row.target_table_column, tags),
      filter: 'text',
      filterPlaceholder: 'Filter...',
      cell: (row) => (
        <OverflowCell
          content={formatAuditLogValue(row.previous_value, row.target_table, row.target_table_column, tags)}
        />
      ),
      width: '130px',
    },
    {
      id: 'new_value',
      header: 'New',
      sortable: true,
      accessor: (row) =>
        formatAuditLogValue(row.new_value, row.target_table, row.target_table_column, tags),
      filter: 'text',
      filterPlaceholder: 'Filter...',
      cell: (row) => (
        <OverflowCell
          content={formatAuditLogValue(row.new_value, row.target_table, row.target_table_column, tags)}
        />
      ),
      width: '130px',
    },
    {
      id: 'impersonating_user_name',
      header: 'Impersonating',
      sortable: true,
      accessor: (row) => row.impersonating_user_name || '-',
      filter: 'text',
      filterPlaceholder: 'Filter...',
      width: '130px',
    },
  ], [tags]);

  // The previous implementation called e.currentTarget.blur() to clear Bootstrap's sticky
  // focus ring after a click. Prism buttons use :focus-visible, so no manual blur is needed.
  const handleClearAllFilters = () => {
    setTableFilter([]);
    setColumnFilter([]);
    setGridKey((k) => k + 1);
  };

  const handleExport = async () => {
    // Use the filtered data from ref
    const dataToExport = filteredDataRef.current;
    
    // Create a new workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'CLEAR Portal';
    workbook.created = new Date();
    workbook.subject = 'Audit Logs';
    
    const worksheet = workbook.addWorksheet('Audit Logs');
    
    // Define columns (without fixed widths - will be calculated later)
    worksheet.columns = [
      { header: 'Timestamp (UTC)', key: 'timestamp' },
      { header: 'User', key: 'user' },
      { header: 'Target', key: 'targetUser' },
      { header: 'Table', key: 'table' },
      { header: 'Column', key: 'column' },
      { header: 'Previous', key: 'previous' },
      { header: 'New', key: 'new' },
      { header: 'Impersonating', key: 'impersonating' },
    ];
    
    // Style the header row
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF8F9FA' }
    };
    
    // Add data rows
    dataToExport.forEach(log => {
      let previousValue: string | number = formatAuditLogValue(log.previous_value, log.target_table, log.target_table_column, tags);
      let newValue: string | number = formatAuditLogValue(log.new_value, log.target_table, log.target_table_column, tags);
      
      // Convert sort_order values to numbers to avoid Excel warnings
      if (log.target_table_column === 'sort_order') {
        if (previousValue && previousValue !== '(empty)' && !isNaN(Number(previousValue))) {
          previousValue = Number(previousValue);
        }
        if (newValue && newValue !== '(empty)' && !isNaN(Number(newValue))) {
          newValue = Number(newValue);
        }
      }
      
      const row = worksheet.addRow({
        timestamp: DateTime.fromISO(log.created_timestamp).toUTC().toFormat('yyyy-MM-dd HH:mm:ss'),
        user: log.source_user_name,
        targetUser: log.target_user_name || '-',
        table: log.target_table,
        column: log.target_table_column,
        previous: previousValue,
        new: newValue,
        impersonating: log.impersonating_user_name || '-',
      });
      
      // Apply background colors to impersonated rows
      if (log.impersonating_user_name) {
        // Blue for login only, orange for actual changes made while impersonating
        const isLoginOnly = log.target_table === 'user_sim' && log.target_table_column === 'sim_user_id';
        const fillColor = isLoginOnly ? 'FFEBF4FB' : 'FFFFE4CC'; // Light blue for login, light orange for changes
        
        row.eachCell((cell) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: fillColor }
          };
        });
      }
    });
    
    // Auto-fit column widths based on content
    worksheet.columns.forEach((column, columnIndex) => {
      let maxLength = 0;
      const columnNumber = columnIndex + 1;
      
      // Check each row in this column
      for (let i = 1; i <= worksheet.rowCount; i++) {
        const cell = worksheet.getCell(i, columnNumber);
        if (cell.value) {
          const cellValue = cell.value.toString();
          // Calculate approximate width (accounting for characters that take more space)
          const cellLength = cellValue.length * 1.1; // Add 10% for font variations
          if (cellLength > maxLength) {
            maxLength = cellLength;
          }
        }
      }
      
      // Set width with padding (minimum 12, maximum 60)
      column.width = Math.min(Math.max(maxLength + 3, 12), 60);
    });
    
    // Freeze the header row
    worksheet.views = [
      { state: 'frozen', xSplit: 0, ySplit: 1, activeCell: 'A2' }
    ];
    
    // Enable auto-filter on header row
    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: worksheet.rowCount, column: 8 }
    };
    
    // Generate filename with current date/time
    const filename = `clear_audit_logs_${DateTime.now().toFormat('yyyy-MM-dd_HHmmss')}.xlsx`;
    
    // Write to buffer and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="clr:text-center clr:my-12">
        <PrismLoading as="spinner" container="inline" />
      </div>
    );
  }

  if (error) {
    return (
      <PrismBanner tone="danger" variant="system" dismissible={false}>
        <strong>Error:</strong> {error}
      </PrismBanner>
    );
  }

  return (
    <AdminPane as="div" width="full" className="audit-logs-container">
      <div className="audit-logs-header">
        <div className="audit-logs-header-row">
          <PrismBanner tone="info" variant="system" dismissible={false} className="audit-logs-intro">
            <strong>Audit Logs:</strong> A record of all officer and admin actions — role changes, settings updates, approvals, reminders, and more. Use the filters to narrow down specific activity.
          </PrismBanner>
          <PrismButton
            tone="secondary"
            display="outline"
            onPrismClick={handleClearAllFilters}
            className="clear-filters-button"
          >
            <img src={clearLogoSrc} alt="CLEAR" className="clear-logo" />
            All Filters
          </PrismButton>
        </div>
      </div>

      {logs.length === 0 ? (
        <PrismBanner tone="info" variant="system" dismissible={false}>No audit logs found.</PrismBanner>
      ) : (
        <>
          <div className="audit-logs-toolbar-filters">
            <div className="audit-logs-toolbar-filter">
              <label className="audit-logs-toolbar-label">Table</label>
              <MultiSelectFilter
                selected={tableFilter}
                onChange={setTableFilter}
                options={uniqueTables}
                allLabel="All Tables"
                filterIdPrefix="table-filter"
              />
            </div>
            <div className="audit-logs-toolbar-filter">
              <label className="audit-logs-toolbar-label">Column</label>
              <MultiSelectFilter
                selected={columnFilter}
                onChange={setColumnFilter}
                options={uniqueColumns}
                allLabel="All Columns"
                filterIdPrefix="column-filter"
              />
            </div>
          </div>

          <div className="audit-logs-table-wrapper">
            <PrismDataGrid
              key={gridKey}
              keyField="id"
              caption="Audit logs"
              data={visibleLogs}
              columns={columns}
              pageSize={50}
              pageSizeOptions={[25, 50, 100, 250]}
              defaultSort={[{ id: 'created_timestamp', desc: true }]}
              emptyMessage="No audit logs match the current filters."
              onFilteredRowsChange={handleFilteredRowsChange}
              rowStyle={(row) => {
                if (!row.impersonating_user_name) return undefined;
                // Blue for login only, orange for actual changes made while impersonating
                const isLoginOnly =
                  row.target_table === 'user_sim' &&
                  row.target_table_column === 'sim_user_id';
                return {
                  'background-color': isLoginOnly
                    ? 'var(--color-primary-tint)'
                    : 'var(--color-warning-tint)',
                };
              }}
            />
          </div>

          <div className="audit-logs-footer">
            <div className="clr:flex clr:justify-between clr:items-center">
              <div className="pagination-info">
                Showing {filteredCount} of {logs.length} records
              </div>
              <div className="export-controls clr:flex clr:items-center clr:justify-end">
                <PrismButton
                  tone="secondary"
                  className="export-button"
                  onPrismClick={handleExport}
                >
                  Export Current View
                </PrismButton>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminPane>
  );
};

export default AuditLogs;
